import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Divider,
  CircularProgress,
  IconButton,
  Button,
  Radio,
  Checkbox,
  Chip,
} from '@mui/material';
import { Add, Remove, Close, ShoppingCart, Edit, StickyNote2 } from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc, orderBy } from '../services/firestoreCompat';
import { db } from '../services/firebaseConfig';
import { useLanguage } from '../contexts/LanguageContext';

interface AddOn {
  id: string;
  name: string;
  name_de: string;
  price: number;
}

// Domino's-style modifier group + option types (mirrors admin service)
interface ModifierOption {
  id: string;
  name: string;
  name_de?: string;
  price_delta: number;
  price_delta_by_size?: { [sizeOptionId: string]: number };
  is_default: boolean;
  is_available: boolean;
  display_order: number;
}

interface ModifierGroup {
  id: string;
  name: string;
  name_de?: string;
  type: 'single' | 'multi';
  required: boolean;
  min_select?: number;
  max_select?: number;
  applies_to_size: boolean;
  display_order: number;
  options: ModifierOption[];
}

// What gets saved on the cart line
export interface CartSelections {
  // groupId → array of optionIds (length 1 for single, N for multi)
  [groupId: string]: string[];
}

interface AddItemWithAddOnsModalProps {
  open: boolean;
  onClose: () => void;
  product: any;
  onAddToCart: (product: any, selectedAddOns: AddOn[], notes: string, extra?: { selections?: CartSelections; modifierGroups?: ModifierGroup[]; resolvedPrice?: number }) => void;
  onUpdateCartItem?: (cartId: number, product: any, selectedAddOns: AddOn[], notes: string, extra?: { selections?: CartSelections; modifierGroups?: ModifierGroup[]; resolvedPrice?: number }) => void;
  language: 'en' | 'ar';
  cartItem?: any;
  cartId?: number;
}

const pickLabel = (obj: { name: string; name_de?: string }, lang: 'en' | 'ar') => {
  if (lang === 'ar') return obj.name_de || obj.name;
  return obj.name;
};

const AddItemWithAddOnsModal: React.FC<AddItemWithAddOnsModalProps> = ({
  open,
  onClose,
  product,
  onAddToCart,
  onUpdateCartItem,
  language,
  cartItem,
  cartId,
}) => {
  const { t } = useLanguage();
  const [availableAddOns, setAvailableAddOns] = useState<AddOn[]>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<Map<string, number>>(new Map());
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Modifier groups state
  const [modifierGroups, setModifierGroups] = useState<ModifierGroup[]>([]);
  const [selections, setSelections] = useState<CartSelections>({});

  useEffect(() => {
    if (open && product) {
      loadAvailableAddOns();
      loadModifierGroups();

      if (cartItem && cartId !== undefined) {
        setIsEditing(true);
        const existingAddOns = new Map<string, number>();
        if (cartItem.addons && Array.isArray(cartItem.addons)) {
          cartItem.addons.forEach((addon: any) => {
            const qty = addon.quantity || 1;
            existingAddOns.set(addon.id, qty);
          });
        }
        setSelectedAddOns(existingAddOns);
        setNotes(cartItem.notes || '');
        if (cartItem.selections && typeof cartItem.selections === 'object') {
          setSelections(cartItem.selections);
        } else {
          setSelections({});
        }
      } else {
        setIsEditing(false);
        setSelectedAddOns(new Map());
        setNotes('');
        setSelections({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product, cartItem, cartId]);

  // Once modifier groups load and we're NOT editing, pre-select defaults (Tomaten etc.)
  useEffect(() => {
    if (!isEditing && modifierGroups.length > 0 && Object.keys(selections).length === 0) {
      const initial: CartSelections = {};
      modifierGroups.forEach((g) => {
        const defaults = g.options.filter((o) => o.is_default && o.is_available).map((o) => o.id);
        if (defaults.length > 0) {
          initial[g.id] = g.type === 'single' ? [defaults[0]] : defaults;
        } else if (g.required && g.type === 'single' && g.options.length > 0) {
          // No default but required single — pre-pick first available so price shows something sensible
          const firstAvail = g.options.find((o) => o.is_available);
          if (firstAvail) initial[g.id] = [firstAvail.id];
        }
      });
      setSelections(initial);
    }
  }, [modifierGroups, isEditing, selections]);

  const loadModifierGroups = async () => {
    if (!product) return;
    const groupIds: string[] = Array.isArray(product.modifier_group_ids) ? product.modifier_group_ids : [];
    if (groupIds.length === 0) {
      setModifierGroups([]);
      return;
    }
    try {
      const groups: ModifierGroup[] = [];
      for (const gid of groupIds) {
        const gSnap = await getDoc(doc(db, 'modifier_groups', gid));
        if (!gSnap.exists()) continue;
        const g = gSnap.data() as any;
        if (g.is_active === false) continue;

        const optsSnap = await getDocs(query(collection(db, 'modifier_options'), where('group_id', '==', gid)));
        const options: ModifierOption[] = [];
        optsSnap.forEach((od) => {
          const o = od.data() as any;
          if (o.is_available === false) return;
          options.push({
            id: od.id,
            name: o.name || '',
            name_de: o.name_de || '',
            price_delta: parseFloat(o.price_delta || 0),
            price_delta_by_size: o.price_delta_by_size || undefined,
            is_default: !!o.is_default,
            is_available: o.is_available !== false,
            display_order: o.display_order || 0,
          });
        });
        options.sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name));

        groups.push({
          id: gSnap.id,
          name: g.name || '',
          name_de: g.name_de || '',
          type: g.type === 'multi' ? 'multi' : 'single',
          required: !!g.required,
          min_select: g.min_select,
          max_select: g.max_select,
          applies_to_size: !!g.applies_to_size,
          display_order: g.display_order || 0,
          options,
        });
      }
      groups.sort((a, b) => a.display_order - b.display_order);
      setModifierGroups(groups);
    } catch (err) {
      console.error('[POS] Failed to load modifier groups:', err);
    }
  };

  const loadAvailableAddOns = async () => {
    if (!product || !product.id) return;

    try {
      setLoading(true);
      setError(null);

      const menuItemAddOnsRef = collection(db, 'menu_item_addons');
      const q = query(menuItemAddOnsRef, where('menu_item_id', '==', product.id));
      const snapshot = await getDocs(q);

      const addonIds: string[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.addon_id) addonIds.push(data.addon_id);
      });

      if (addonIds.length > 0) {
        const addons: AddOn[] = [];
        for (const addonId of addonIds) {
          try {
            const addonDocRef = doc(db, 'addons', addonId);
            const addonDocSnap = await getDoc(addonDocRef);
            if (addonDocSnap.exists()) {
              const data = addonDocSnap.data();
              if (data.is_active !== false) {
                addons.push({
                  id: addonDocSnap.id,
                  name: data.name || '',
                  name_de: data.name_de || data.name || '',
                  price: parseFloat(data.price || 0),
                });
              }
            }
          } catch (err) {
            console.warn(`Could not fetch addon ${addonId}:`, err);
          }
        }
        if (addons.length > 0) {
          setAvailableAddOns(addons);
          setLoading(false);
          return;
        }
      }

      // Fallback: fetch all active add-ons
      const addonsRef = collection(db, 'addons');
      let allAddonsSnapshot;
      try {
        allAddonsSnapshot = await getDocs(query(addonsRef, where('is_active', '==', true), orderBy('sort_order'), orderBy('name')));
      } catch (err) {
        allAddonsSnapshot = await getDocs(query(addonsRef, where('is_active', '==', true)));
      }

      const addons: AddOn[] = [];
      allAddonsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.is_active !== false) {
          addons.push({
            id: doc.id,
            name: data.name || '',
            name_de: data.name_de || data.name || '',
            price: parseFloat(data.price || 0),
          });
        }
      });
      setAvailableAddOns(addons);
    } catch (err: any) {
      console.error('Error loading add-ons:', err);
      setError(err.message || 'Failed to load add-ons');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOnQuantityChange = (addonId: string, delta: number) => {
    setSelectedAddOns(prev => {
      const newMap = new Map(prev);
      const currentQty = newMap.get(addonId) || 0;
      const newQty = Math.max(0, currentQty + delta);
      if (newQty === 0) newMap.delete(addonId);
      else newMap.set(addonId, newQty);
      return newMap;
    });
  };

  // --- Modifier handlers ---
  const toggleModifierOption = (group: ModifierGroup, optionId: string) => {
    setSelections((prev) => {
      const current = prev[group.id] || [];
      let next: string[];
      if (group.type === 'single') {
        // Radio: allow deselecting only if NOT required
        if (current[0] === optionId) {
          next = group.required ? current : [];
        } else {
          next = [optionId];
        }
      } else {
        // Checkbox
        if (current.includes(optionId)) {
          next = current.filter((id) => id !== optionId);
        } else {
          if (group.max_select && current.length >= group.max_select) {
            return prev;
          }
          next = [...current, optionId];
        }
      }
      return { ...prev, [group.id]: next };
    });
  };

  // --- Pricing ---
  const sizeGroup = modifierGroups.find((g) => g.applies_to_size);
  const sizeOptionId: string | null = sizeGroup ? (selections[sizeGroup.id]?.[0] || null) : null;

  // Resolve base price: size option's price_delta (treated as the size's full price) if sizeGroup exists,
  // else product's own price.
  let basePrice = product?.price_with_tax || parseFloat(product?.base_price) || parseFloat(product?.price) || 0;
  if (sizeGroup && sizeOptionId) {
    const sizeOpt = sizeGroup.options.find((o) => o.id === sizeOptionId);
    if (sizeOpt) {
      // If size option specifies a delta > 0, use it as absolute price; else keep base
      basePrice = sizeOpt.price_delta > 0 ? sizeOpt.price_delta : basePrice;
    }
  }

  // Sum deltas from all non-size groups
  let modifierDeltas = 0;
  modifierGroups.forEach((g) => {
    if (g.applies_to_size) return;
    const picked = selections[g.id] || [];
    picked.forEach((optId) => {
      const opt = g.options.find((o) => o.id === optId);
      if (!opt) return;
      if (sizeOptionId && opt.price_delta_by_size && opt.price_delta_by_size[sizeOptionId] !== undefined) {
        modifierDeltas += opt.price_delta_by_size[sizeOptionId];
      } else {
        modifierDeltas += opt.price_delta;
      }
    });
  });

  const itemPrice = basePrice + modifierDeltas;
  const addonsTotal = Array.from(selectedAddOns.entries()).reduce((sum, [addonId, quantity]) => {
    const addon = availableAddOns.find(a => a.id === addonId);
    return sum + (addon ? addon.price * quantity : 0);
  }, 0);
  const grandTotal = itemPrice + addonsTotal;
  const selectedCount = Array.from(selectedAddOns.values()).reduce((s, q) => s + q, 0);

  // Validation: required groups must have a selection
  const missingRequired = modifierGroups.filter((g) => {
    if (!g.required) return false;
    const picked = selections[g.id] || [];
    if (g.type === 'single') return picked.length === 0;
    const min = g.min_select ?? 1;
    return picked.length < min;
  });

  const canSave = missingRequired.length === 0;

  const handleSave = () => {
    if (!canSave) return;
    const selectedAddOnsList: Array<AddOn & { quantity: number }> = [];
    selectedAddOns.forEach((quantity, addonId) => {
      const addon = availableAddOns.find(a => a.id === addonId);
      if (addon) selectedAddOnsList.push({ ...addon, quantity });
    });

    const extra = modifierGroups.length > 0 ? { selections, modifierGroups, resolvedPrice: itemPrice } : undefined;

    if (isEditing && onUpdateCartItem && cartId !== undefined) {
      onUpdateCartItem(cartId, product, selectedAddOnsList, notes, extra);
    } else {
      onAddToCart(product, selectedAddOnsList, notes, extra);
    }
    onClose();
  };

  if (!open || !product) return null;

  const displayName = language === 'ar' ? (product.name_de || product.name) : product.name;
  const displayDescription = language === 'ar' ? (product.description_de || product.description) : product.description;

  return (
    <Box
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      sx={{
        position: 'fixed',
        top: { xs: 56, sm: 0 }, left: 0, right: 0, bottom: 0,
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'center',
        zIndex: 1300,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          backgroundColor: '#0A2540',
          borderRadius: { xs: 0, sm: '10px', lg: '16px' },
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          maxWidth: { xs: '100%', sm: '420px', lg: '560px' },
          width: { xs: '100%', sm: '70%', lg: '92%' },
          height: { xs: '100%', sm: 'auto' },
          maxHeight: { xs: '100%', sm: '80vh', lg: '88vh' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #558B2F 0%, #7CB342 100%)',
          p: { xs: 1.5, lg: 2.5 },
          position: 'relative',
        }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute', top: { xs: 6, lg: 12 }, right: { xs: 6, lg: 12 },
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
            size="small"
          >
            <Close sx={{ fontSize: { xs: 18, lg: 24 } }} />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            {isEditing ? (
              <Edit sx={{ fontSize: { xs: 14, lg: 18 }, color: 'rgba(255,255,255,0.6)' }} />
            ) : (
              <ShoppingCart sx={{ fontSize: { xs: 14, lg: 18 }, color: 'rgba(255,255,255,0.6)' }} />
            )}
            <Typography sx={{ fontSize: { xs: '0.6rem', lg: '0.75rem' }, fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '1px' }}>
              {isEditing ? t('addons.edit_item') : t('addons.add_item_to_cart')}
            </Typography>
          </Box>

          <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: { xs: '0.95rem', lg: '1.25rem' }, lineHeight: 1.3, pr: 3 }}>
            {displayName}
          </Typography>

          {displayDescription && (
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.68rem', lg: '0.82rem' }, mt: 0.25, lineHeight: 1.3, display: { xs: '-webkit-box', lg: 'block' }, WebkitLineClamp: { xs: 2, lg: 10 }, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {displayDescription}
            </Typography>
          )}

          <Typography sx={{
            mt: { xs: 0.75, lg: 1.5 },
            display: 'inline-block',
            backgroundColor: 'rgba(255,255,255,0.15)',
            color: '#fff',
            fontWeight: 700,
            fontSize: { xs: '0.85rem', lg: '1.1rem' },
            px: { xs: 1, lg: 1.5 }, py: 0.3,
            borderRadius: '6px',
          }}>
            €{itemPrice.toFixed(2)}
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', p: { xs: 1.5, lg: 2.5 } }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {error && (
            <Box sx={{ p: 2, mb: 2, backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '10px' }}>
              <Typography color="error" variant="body2">{error}</Typography>
            </Box>
          )}

          {!loading && !error && (
            <>
              {/* Modifier groups (Size, Crust, Sauce, Toppings, etc.) */}
              {modifierGroups.map((g) => {
                const picked = selections[g.id] || [];
                const isMissing = missingRequired.includes(g);
                return (
                  <Box key={g.id} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: isMissing ? '#ef4444' : '#e0e0e0' }}>
                        {pickLabel(g, language)}
                      </Typography>
                      {g.required ? (
                        <Chip size="small" label={t('modifiers.required') || 'Pflichtfeld'} sx={{ bgcolor: '#2a2d3e', color: '#ff9800', fontSize: '0.65rem', height: 18 }} />
                      ) : (
                        <Chip size="small" label={t('modifiers.optional') || 'Optional'} sx={{ bgcolor: '#1e2139', color: '#6b7280', fontSize: '0.65rem', height: 18 }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {g.options.map((opt) => {
                        const selected = picked.includes(opt.id);
                        const displayDelta = (sizeOptionId && opt.price_delta_by_size?.[sizeOptionId] !== undefined)
                          ? opt.price_delta_by_size[sizeOptionId]
                          : opt.price_delta;
                        return (
                          <Box
                            key={opt.id}
                            onClick={() => toggleModifierOption(g, opt.id)}
                            sx={{
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              justifyContent: 'space-between',
                              p: { xs: 1, lg: 1.25 },
                              borderRadius: '10px',
                              border: selected ? '2px solid #00C896' : '1px solid #2a2d3e',
                              backgroundColor: selected ? 'rgba(0, 200, 150, 0.08)' : '#1e2139',
                              '&:hover': { borderColor: selected ? '#00C896' : '#3a3d52' },
                            }}
                          >
                            {g.type === 'single' ? (
                              <Radio
                                checked={selected}
                                onChange={() => toggleModifierOption(g, opt.id)}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{ p: 0.5, color: '#6b7280', '&.Mui-checked': { color: '#00C896' } }}
                              />
                            ) : (
                              <Checkbox
                                checked={selected}
                                onChange={() => toggleModifierOption(g, opt.id)}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{ p: 0.5, color: '#6b7280', '&.Mui-checked': { color: '#00C896' } }}
                              />
                            )}
                            <Typography sx={{ flex: 1, fontSize: '0.85rem', color: '#e0e0e0', fontWeight: selected ? 600 : 500 }}>
                              {pickLabel(opt, language)}
                            </Typography>
                            {displayDelta !== 0 && (
                              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: selected ? '#00C896' : '#9ca3af', flexShrink: 0 }}>
                                {g.applies_to_size ? `€${displayDelta.toFixed(2)}` : (displayDelta > 0 ? `+€${displayDelta.toFixed(2)}` : `−€${Math.abs(displayDelta).toFixed(2)}`)}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}

              {/* Legacy flat add-ons: hidden when modifier groups exist OR the item is a
                  Pizza (pizzas are migrated to the structured group flow exclusively, per
                  client request — never mix the old add-ons list into pizza). */}
              {(() => {
                const categoryStr = (product?.category_name || product?.category || '').toString().toLowerCase();
                const isPizza = categoryStr === 'pizza' || categoryStr.startsWith('pizza');
                const suppressLegacy = isPizza || modifierGroups.length > 0;
                return !suppressLegacy && availableAddOns.length > 0;
              })() ? (
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px', mb: 1.5 }}>
                    {t('addons.select_addons')}
                    <Typography component="span" sx={{ fontWeight: 400, fontSize: '0.7rem', color: '#6b7280', ml: 0.5, textTransform: 'none', letterSpacing: 0 }}>
                      ({t('addons.optional') || 'Optional'})
                    </Typography>
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {availableAddOns.map((addon) => {
                      const quantity = selectedAddOns.get(addon.id) || 0;
                      const addonDisplayName = language === 'ar' ? addon.name_de : addon.name;
                      const isSelected = quantity > 0;

                      return (
                        <Box
                          key={addon.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: { xs: 1, lg: 1.5 },
                            borderRadius: { xs: '8px', lg: '10px' },
                            border: isSelected ? '2px solid #00C896' : '1px solid #2a2d3e',
                            backgroundColor: isSelected ? 'rgba(0, 200, 150, 0.08)' : '#1e2139',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              borderColor: isSelected ? '#00C896' : '#3a3d52',
                              backgroundColor: isSelected ? 'rgba(0, 200, 150, 0.12)' : '#252838',
                            },
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: isSelected ? 600 : 500, fontSize: { xs: '0.78rem', lg: '0.9rem' }, color: '#e0e0e0' }}>
                              {addonDisplayName}
                            </Typography>
                            <Typography sx={{ fontSize: { xs: '0.68rem', lg: '0.78rem' }, fontWeight: 600, color: isSelected ? '#00C896' : '#6b7280' }}>
                              +€{addon.price.toFixed(2)} {t('addons.each')}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleAddOnQuantityChange(addon.id, -1)}
                              disabled={quantity === 0}
                              sx={{
                                width: 30, height: 30,
                                border: '1px solid #3a3d52',
                                borderRadius: '8px',
                                backgroundColor: '#252838',
                                color: '#9ca3af',
                                '&:hover': { backgroundColor: '#3a3d52', borderColor: '#ef4444' },
                                '&.Mui-disabled': { opacity: 0.3 },
                              }}
                            >
                              <Remove sx={{ fontSize: 16 }} />
                            </IconButton>

                            <Box sx={{
                              minWidth: 32, textAlign: 'center',
                              fontWeight: 700, fontSize: '0.95rem',
                              color: isSelected ? '#00C896' : '#6b7280',
                            }}>
                              {quantity}
                            </Box>

                            <IconButton
                              size="small"
                              onClick={() => handleAddOnQuantityChange(addon.id, 1)}
                              sx={{
                                width: 30, height: 30,
                                border: '1px solid #00C896',
                                borderRadius: '8px',
                                backgroundColor: 'rgba(0, 200, 150, 0.1)',
                                color: '#00C896',
                                '&:hover': { backgroundColor: 'rgba(0, 200, 150, 0.2)' },
                              }}
                            >
                              <Add sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : modifierGroups.length === 0 ? (
                <Box sx={{ p: 2.5, backgroundColor: '#1e2139', borderRadius: '10px', textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    {t('addons.no_addons_available')}
                  </Typography>
                </Box>
              ) : null}

              {/* Notes */}
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StickyNote2 sx={{ fontSize: 14 }} />
                  {t('addons.notes_optional')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('addons.notes_placeholder') || 'Special requests, allergies...'}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: '#1e2139',
                      fontSize: '0.9rem',
                      color: '#e0e0e0',
                      '& fieldset': { borderColor: '#2a2d3e' },
                      '&:hover fieldset': { borderColor: '#3a3d52' },
                      '&.Mui-focused fieldset': { borderColor: '#00C896', borderWidth: '2px' },
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ borderTop: '1px solid #2a2d3e' }}>
          <Box sx={{ px: { xs: 1.5, lg: 2.5 }, pt: { xs: 1, lg: 2 }, pb: { xs: 0.75, lg: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography sx={{ fontSize: { xs: '0.72rem', lg: '0.85rem' }, color: '#9ca3af' }}>{t('addons.item_label')}</Typography>
              <Typography sx={{ fontSize: { xs: '0.72rem', lg: '0.85rem' }, fontWeight: 600, color: '#e0e0e0' }}>€{itemPrice.toFixed(2)}</Typography>
            </Box>
            {selectedCount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography sx={{ fontSize: { xs: '0.72rem', lg: '0.85rem' }, color: '#9ca3af' }}>
                  {t('addons.addons_label')} ({selectedCount})
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.72rem', lg: '0.85rem' }, fontWeight: 600, color: '#00C896' }}>
                  +€{addonsTotal.toFixed(2)}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: { xs: 0.5, lg: 1 }, borderColor: '#2a2d3e' }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', lg: '1rem' }, color: '#e0e0e0' }}>
                {t('dashboard.total')}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', lg: '1.3rem' }, color: '#00C896' }}>
                €{grandTotal.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{
            display: 'flex', gap: { xs: 0.75, lg: 1.5 },
            px: { xs: 1.5, lg: 2.5 }, pb: { xs: 1.5, lg: 2.5 }, pt: { xs: 0.5, lg: 1 },
          }}>
            <Button
              onClick={onClose}
              fullWidth
              sx={{
                py: { xs: 0.7, lg: 1.4 },
                borderRadius: { xs: '8px', lg: '12px' },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', lg: '0.95rem' },
                minHeight: { xs: 32, lg: 44 },
                color: '#9ca3af',
                backgroundColor: '#1e2139',
                border: '1px solid #2a2d3e',
                touchAction: 'manipulation',
                '&:hover': { backgroundColor: '#252838' },
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              fullWidth
              disabled={loading || !canSave}
              sx={{
                py: { xs: 0.7, lg: 1.4 },
                borderRadius: { xs: '8px', lg: '12px' },
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '0.75rem', lg: '0.95rem' },
                minHeight: { xs: 32, lg: 44 },
                color: '#fff',
                touchAction: 'manipulation',
                background: 'linear-gradient(135deg, #558B2F 0%, #7CB342 100%)',
                boxShadow: '0 4px 14px rgba(85, 139, 47, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #689F38 0%, #8BC34A 100%)',
                },
                '&:disabled': {
                  background: '#2a2d3e',
                  color: '#6b7280',
                  boxShadow: 'none',
                },
              }}
            >
              {!canSave
                ? (t('modifiers.choose_required') || 'Please choose required options')
                : isEditing
                  ? t('addons.save_changes')
                  : `${t('addons.add_to_cart')} · €${grandTotal.toFixed(2)}`}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddItemWithAddOnsModal;
